import { FormTypeId, PjfiFormTypes } from '@/components/report-forms/constants/formTypeId';
import type { JsonReportForm, ReportDetails } from '../models';
import type { JsonSchemaBase } from '@/json-validation/types/jsonSchemaBase';
import { ReportDataModel } from '@/constants/reportDataModel';

export const JsonConfigRoute = './form-config';

export enum ReportType {
  PJFI = 'pjfi',
  MJFI = 'mjfi',
  BUDGET = 'budget'
}

export function getJsonConfigRoute(reportType: ReportType, version: number) {
  return `${JsonConfigRoute}/${reportType}/versions/${version}`;
}

export function getReportTypeForFormTypeId(formTypeId: number) {
  if (PjfiFormTypes.includes(formTypeId as (typeof PjfiFormTypes)[number])) {
    return ReportType.PJFI;
  }
  if (FormTypeId.isBudgetForm(formTypeId)) {
    return ReportType.BUDGET;
  }
  return ReportType.MJFI;
}

export const FormTypeIdFileNameJsonMapper = {
  [FormTypeId.FormEoOne]: '1_eo1_ui.json',
  [FormTypeId.FormEoOneAAssets]: '2_eo2_ui.json',
  [FormTypeId.FormEoOneALiabilities]: '3_eo2_ui.json',
  [FormTypeId.FormEoOneB]: '4_eo3_ui.json',
  [FormTypeId.FormTen]: '5_eo4_ui.json',
  [FormTypeId.FormEleven]: '6_eo5_ui.json',
  [FormTypeId.FormTwelve]: '7_eo6_ui.json',
  [FormTypeId.FormThirteen]: '8_eo7_ui.json',
  [FormTypeId.FormFourteen]: '9_eo8_ui.json',
  [FormTypeId.FormFifteen]: '10_eo9_ui.json',
  [FormTypeId.FormSixteen]: '10_eo10_ui.json',
  [FormTypeId.FormSeventeen]: '11_eo11_ui.json',
  [FormTypeId.FormEighteen]: '12_eo12_ui.json',
  [FormTypeId.FormNineteen]: '13_eo13_ui.json',
  [FormTypeId.FormTwenty]: '14_eo14_ui.json',
  [FormTypeId.FormZeroJson]: '1_bs_ui.json',
  [FormTypeId.FormOneJson]: '2_bs_ui.json',
  [FormTypeId.FormTwoJson]: '2_bs_ui.json',
  [FormTypeId.FormFourJson]: '4_nt_ui.json',
  [FormTypeId.FormFiveJson]: '5_ib_ui.json'
} as const;

export async function getJsonConfigForFormTypeId(
  formTypeId: keyof typeof FormTypeIdFileNameJsonMapper,
  version: string | number
) {
  const reportType = getReportTypeForFormTypeId(formTypeId);
  const modules = import.meta.glob('./*/versions/*/*.json');
  const fileName = FormTypeIdFileNameJsonMapper[formTypeId];
  const key = `./${reportType}/versions/${version}/${fileName}`;

  const loader = modules[key] as undefined | (() => Promise<{ default: unknown }>);
  if (!loader) {
    throw new Error(`Config not found for key: ${key}`);
  }

  const mod = await loader();
  return (mod as { default: unknown }).default;
}

export async function getJsonConfigForFormId(
  form: JsonReportForm
) {
  const config = await getJsonConfigForFormTypeId(form.formTypeId as keyof typeof FormTypeIdFileNameJsonMapper, form.version);
  const parsedJsonSchema = JSON.parse(form.jsonSchema || '{}');
  const updatedJsonSchema = {
    ...parsedJsonSchema,
    ...config as unknown as JsonSchemaBase
  };
  return {
    ...form,
    jsonSchema: JSON.stringify(updatedJsonSchema)
  };
}

export async function getJsonConfigForFormTypeIdOrNull(
  formTypeId: number,
  version: string | number
): Promise<JsonSchemaBase | null> {
  if (!(formTypeId in FormTypeIdFileNameJsonMapper)) {
    return null;
  }

  try {
    return await getJsonConfigForFormTypeId(
      formTypeId as keyof typeof FormTypeIdFileNameJsonMapper,
      version
    ) as JsonSchemaBase;
  } catch {
    return null;
  }
}

export async function addJsonConfigToForm(
  form: { formTypeId: number; version: string | number; jsonSchema: string | null }
): Promise<{ jsonSchema: string }> {
  const jsonConfig = await getJsonConfigForFormTypeIdOrNull(
    form.formTypeId,
    form.version || 1
  );
  if (!jsonConfig) {
    return {
      jsonSchema: form.jsonSchema || '{}'
    };
  }

  const existingSchema = JSON.parse(form.jsonSchema || '{}');
  const updatedSchema: JsonSchemaBase = {
    ...existingSchema,
    ...jsonConfig
  };

  return {
    jsonSchema: JSON.stringify(updatedSchema)
  };
}

export async function mapJsonConfiguration(report: ReportDetails): Promise<ReportDetails> {
  if (report.dataModel === ReportDataModel.AOP_FORM) {
    return report;
  }

  const jsonForms = await Promise.all(report.jsonForms.filter(Boolean).map(async (form) => {
    const { jsonSchema } = await addJsonConfigToForm({
      formTypeId: form.formTypeId,
      version: form.version,
      jsonSchema: form.jsonSchema
    });
    return {
      ...form,
      jsonSchema
    };
  }));

  return {
    ...report,
    jsonForms
  };
}
